import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { changeLocalPassword } from './accountSecurity';
import { shouldUseLocalDevFallback } from './authSession';
import { isRealGatewayAccessToken } from './gatewayAuth';
import { postludeSessionId } from './postludeVisit';
import { clearFunnel, trackUxEvent } from './uxTelemetry';

type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  user: AuthUserProfile | null;
  ssoProvider: 'keycloak' | null;
  startSsoLogin: (redirectPath?: string) => void;
  startGoogleLogin: (redirectPath?: string, options?: { signup?: boolean }) => Promise<{ ok: boolean; error?: string }>;
  startMicrosoftLogin: (redirectPath?: string) => Promise<{ ok: boolean; error?: string }>;
  login: (email: string, password: string) => Promise<{ ok: boolean; error?: string; passwordResetRequired?: boolean }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ ok: boolean; error?: string; warning?: string }>;
  logout: () => void;
  refreshProfile: () => Promise<void>;
};

type CompanyMembership = {
  company_id: number;
  company_name: string;
  roles: string[];
  features: string[];
};

type AuthUserProfile = {
  user_id: string;
  email: string;
  is_platform_admin: boolean;
  platform_roles: string[];
  companies: CompanyMembership[];
  effective_company_id: number | null;
  effective_roles: string[];
  effective_features: string[];
  effective_permissions?: string[];
  managed_mailbox_email?: string | null;
};

const STORAGE_KEY = 'nextcompli.auth.token';
const LEGACY_STORAGE_KEY = 'nextcompli.authenticated';
const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    // Prefer IPv4 loopback locally — Cloudflare WARP / IPv6 localhost can stall fetches.
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }

  if (typeof window === 'undefined') {
    return 'http://127.0.0.1:8000';
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) {
    return 'http://127.0.0.1:8000';
  }

  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};

const GATEWAY_BASE_URL = inferGatewayBaseUrl();
const AUTH_LOGIN_URL = `${GATEWAY_BASE_URL}/api/v1/auth/login`;
const AUTH_ME_URL = `${GATEWAY_BASE_URL}/api/v1/auth/me`;
const AUTH_CHANGE_PASSWORD_URL = `${GATEWAY_BASE_URL}/api/v1/auth/change-password`;
const SSO_PROVIDER = (process.env.REACT_APP_SSO_PROVIDER || '').trim().toLowerCase() === 'keycloak' ? 'keycloak' : null;
const AUTH_SSO_START_URL = `${GATEWAY_BASE_URL}/api/v1/auth/sso/keycloak/start`;
const AUTH_GOOGLE_START_URL = `${GATEWAY_BASE_URL}/api/v1/auth/sso/google/start`;
const AUTH_MICROSOFT_START_URL = `${GATEWAY_BASE_URL}/api/v1/auth/sso/microsoft/start`;
// Production login can exceed 8s when gateway syncs WorkMail passwords on @nextcompli aliases.
const AUTH_REQUEST_TIMEOUT_MS = 30000;
// Local session probe should fail fast so /login never sits on "Checking session".
const AUTH_SESSION_CHECK_TIMEOUT_MS = isLocalDevHostStatic() ? 5000 : AUTH_REQUEST_TIMEOUT_MS;

function isLocalDevHostStatic(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
}

const fetchWithTimeout = async (input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = AUTH_REQUEST_TIMEOUT_MS): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const readSsoTokenFromLocation = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash);
  const token = searchParams.get('sso_token') || searchParams.get('access_token') || hashParams.get('sso_token') || hashParams.get('access_token');
  if (!token) {
    return null;
  }

  // Strip SSO transport params from URL once token is persisted.
  searchParams.delete('sso_token');
  searchParams.delete('access_token');
  searchParams.delete('sso_provider');
  hashParams.delete('sso_token');
  hashParams.delete('access_token');
  hashParams.delete('sso_provider');
  const nextSearch = searchParams.toString();
  const nextHash = hashParams.toString();
  const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${nextHash ? `#${nextHash}` : ''}`;
  window.history.replaceState(window.history.state, '', nextUrl);

  return token.trim();
};

const buildProviderStartUrl = (startUrl: string, redirectPath = '/dashboard'): string => {
  if (typeof window === 'undefined') {
    return startUrl;
  }
  const redirectUri = `${window.location.origin}${redirectPath.startsWith('/') ? redirectPath : `/${redirectPath}`}`;
  return `${startUrl}?redirect_uri=${encodeURIComponent(redirectUri)}`;
};

const isLocalDevHost = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const buildLocalDevUser = (email: string): AuthUserProfile => ({
  user_id: 'legacy-local-user',
  email,
  is_platform_admin: true,
  platform_roles: ['platform_admin'],
  companies: [],
  effective_company_id: null,
  effective_roles: ['platform_admin'],
  effective_features: [],
});

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(() => {
    const ssoToken = readSsoTokenFromLocation();
    if (ssoToken) {
      localStorage.setItem(STORAGE_KEY, ssoToken);
      localStorage.setItem(LEGACY_STORAGE_KEY, 'true');
      return ssoToken;
    }

    const storedToken = localStorage.getItem(STORAGE_KEY);
    if (storedToken) {
      return storedToken;
    }
    return localStorage.getItem(LEGACY_STORAGE_KEY) === 'true' ? '__legacy__' : null;
  });
  const [user, setUser] = useState<AuthUserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_STORAGE_KEY) === 'true'));

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    setAccessToken(null);
    setUser(null);
    setIsLoading(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEY);
    const legacyAuthenticated = localStorage.getItem(LEGACY_STORAGE_KEY) === 'true';
    if (!token && legacyAuthenticated) {
      setAccessToken('__legacy__');
      setUser({
        user_id: 'legacy-local-user',
        email: 'legacy.local.user@nextcompli.local',
        is_platform_admin: true,
        platform_roles: ['platform_admin'],
        companies: [],
        effective_company_id: null,
        effective_roles: ['platform_admin'],
        effective_features: [],
      });
      setIsLoading(false);
      return;
    }

    if (!token) {
      setAccessToken(null);
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchWithTimeout(AUTH_ME_URL, {
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }, AUTH_SESSION_CHECK_TIMEOUT_MS);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          logout();
          return;
        }
        if (isRealGatewayAccessToken(token)) {
          setAccessToken(token);
          setIsLoading(false);
          return;
        }
        logout();
        return;
      }

      const payload = (await response.json()) as { user?: AuthUserProfile };
      if (!payload.user) {
        logout();
        return;
      }

      setAccessToken(token);
      setUser(payload.user);
    } catch {
      if (isRealGatewayAccessToken(token)) {
        setAccessToken(token);
        setIsLoading(false);
        return;
      }
      logout();
    } finally {
      setIsLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    void refreshProfile();
  }, [refreshProfile]);

  // Safety valve: never leave protected routes stuck on "Checking session..."
  useEffect(() => {
    if (!isLoading) return undefined;
    const timer = window.setTimeout(() => {
      setIsLoading(false);
    }, AUTH_SESSION_CHECK_TIMEOUT_MS + 1500);
    return () => window.clearTimeout(timer);
  }, [isLoading]);

  const startSsoLogin = useCallback((redirectPath = '/dashboard') => {
    if (typeof window === 'undefined' || SSO_PROVIDER !== 'keycloak') {
      return;
    }
    window.location.assign(buildProviderStartUrl(AUTH_SSO_START_URL, redirectPath));
  }, []);

  const socialProviderReady = useCallback(async (provider: 'google' | 'microsoft') => {
    try {
      const response = await fetchWithTimeout(`${GATEWAY_BASE_URL}/api/v1/auth/sso/providers`, {}, 4000);
      if (!response.ok) {
        return true;
      }
      const body = await response.json() as { google?: boolean; microsoft?: boolean };
      return body[provider] !== false;
    } catch {
      return true;
    }
  }, []);

  const startGoogleLogin = useCallback(async (redirectPath = '/dashboard', options?: { signup?: boolean }) => {
    if (typeof window === 'undefined') {
      return { ok: false, error: 'unavailable' };
    }
    if (!(await socialProviderReady('google'))) {
      return { ok: false, error: 'unavailable' };
    }
    const startUrl = buildProviderStartUrl(AUTH_GOOGLE_START_URL, redirectPath)
      + (options?.signup ? '&signup=1' : '');
    // One navigation. A prefetch also creates a server state, and the second
    // Google return then has nothing left to finish and dumps the person on login.
    window.location.assign(startUrl);
    return { ok: true };
  }, [socialProviderReady]);

  const startMicrosoftLogin = useCallback(async (redirectPath = '/dashboard') => {
    if (typeof window === 'undefined') {
      return { ok: false, error: 'unavailable' };
    }
    if (!(await socialProviderReady('microsoft'))) {
      return { ok: false, error: 'unavailable' };
    }
    window.location.assign(buildProviderStartUrl(AUTH_MICROSOFT_START_URL, redirectPath));
    return { ok: true };
  }, [socialProviderReady]);

  const login = useCallback(async (email: string, password: string) => {
    const tryLocalFallback = (failureKind: 'http' | 'timeout' | 'network') => {
      if (!shouldUseLocalDevFallback({
        isLocalHost: isLocalDevHost(),
        email,
        password,
        failureKind,
        existingToken: localStorage.getItem(STORAGE_KEY),
      })) {
        return null;
      }

      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(LEGACY_STORAGE_KEY, 'true');
      setAccessToken('__legacy__');
      setUser(buildLocalDevUser(email));
      setIsLoading(false);
      clearFunnel();
      trackUxEvent({
        event_type: 'funnel_completed',
        product: 'website',
        funnel_key: 'login',
        step: 'local_fallback',
        last_path: '/login',
        email,
      });
      return { ok: true as const, passwordResetRequired: false };
    };

    try {
      const response = await fetchWithTimeout(AUTH_LOGIN_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Postlude-Session': postludeSessionId(),
        },
        body: JSON.stringify({ email, password }),
      }, AUTH_REQUEST_TIMEOUT_MS);

      const payload = (await response.json().catch(() => ({}))) as {
        access_token?: string;
        user?: AuthUserProfile;
        password_reset_required?: boolean;
        detail?: string;
      };

      if (!response.ok || !payload.access_token || !payload.user) {
        const fallbackResult = tryLocalFallback('http');
        if (fallbackResult) {
          return fallbackResult;
        }
        const detail = String(payload.detail || '').trim();
        const lowered = detail.toLowerCase();
        const loginGuidance = lowered === 'invalid email or password'
          ? 'That password does not match this account. Reset the password if you do not remember it.'
          : lowered.includes('signs in with')
            ? detail
            : '';
        trackUxEvent({
          event_type: 'login_failed',
          product: 'website',
          funnel_key: 'login',
          step: 'submit',
          last_path: '/login',
          email,
          error_code: String(response.status),
          message: detail || 'login_failed',
        });
        return {
          ok: false,
          error: loginGuidance || detail || 'Unable to authenticate. Please check your credentials.',
        };
      }

      localStorage.setItem(STORAGE_KEY, payload.access_token);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      setAccessToken(payload.access_token);
      setUser(payload.user);
      setIsLoading(false);
      clearFunnel();
      trackUxEvent({
        event_type: 'funnel_completed',
        product: 'website',
        funnel_key: 'login',
        step: 'submit',
        last_path: '/login',
        email,
        user_id: payload.user.user_id,
      });

      return {
        ok: true,
        passwordResetRequired: Boolean(payload.password_reset_required),
      };
    } catch (error) {
      const timedOut = error instanceof DOMException && error.name === 'AbortError';
      const fallbackResult = tryLocalFallback(timedOut ? 'timeout' : 'network');
      if (fallbackResult) {
        return fallbackResult;
      }
      trackUxEvent({
        event_type: 'login_failed',
        product: 'website',
        funnel_key: 'login',
        step: 'submit',
        last_path: '/login',
        email,
        error_code: timedOut ? 'timeout' : 'network',
        message: timedOut ? 'timeout' : 'unreachable',
      });
      return {
        ok: false,
        error: timedOut
          ? 'Login timed out talking to the local gateway. If Cloudflare WARP is on, disconnect it or open http://127.0.0.1:3000 and retry. Confirm gateway is up on :8000.'
          : 'Unable to reach authentication service at 127.0.0.1:8000. Please try again.',
      };
    }
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const current = String(currentPassword || '');
    const next = String(newPassword || '');
    if (next.length < 12) {
      return { ok: false, error: 'New password must be at least 12 characters.' };
    }
    if (current === next) {
      return { ok: false, error: 'New password must be different from the current password.' };
    }

    // Local-only demo session (no gateway token).
    if (!accessToken || accessToken === '__legacy__') {
      const updated = changeLocalPassword(current, next);
      if (!updated) {
        return { ok: false, error: 'Current password is incorrect.' };
      }
      return { ok: true };
    }

    try {
      const response = await fetchWithTimeout(AUTH_CHANGE_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          current_password: current,
          new_password: next,
        }),
      });
      const payload = (await response.json().catch(() => ({}))) as {
        detail?: string | Array<{ msg?: string }>;
        keycloak_sync?: { status?: string; reason?: string };
      };
      if (!response.ok) {
        const detail = Array.isArray(payload.detail)
          ? payload.detail.map((row) => row?.msg || '').filter(Boolean).join(' ')
          : String(payload.detail || '').trim();
        if (response.status === 400 || /current password/i.test(detail)) {
          return { ok: false, error: detail || 'Current password is incorrect.' };
        }
        if (response.status === 401 || response.status === 403) {
          return { ok: false, error: 'Your session expired. Sign in again, then change your password.' };
        }
        return { ok: false, error: detail || 'Unable to update password. Please try again.' };
      }

      // Keep local fallback credentials in sync for local hybrid testing.
      try {
        changeLocalPassword(current, next);
      } catch {
        // Ignore local store sync failures in production sessions.
      }

      const warning = payload.keycloak_sync?.status === 'warning'
        ? String(payload.keycloak_sync.reason || 'Identity provider sync warning')
        : undefined;
      return { ok: true, warning };
    } catch (error) {
      const timedOut = error instanceof DOMException && error.name === 'AbortError';
      return {
        ok: false,
        error: timedOut
          ? 'Password change timed out. Confirm the authentication service is reachable and try again.'
          : 'Unable to reach authentication service. Please try again.',
      };
    }
  }, [accessToken]);

  const isAuthenticated = Boolean(accessToken && user);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      accessToken,
      user,
      ssoProvider: SSO_PROVIDER,
      startSsoLogin,
      startGoogleLogin,
      startMicrosoftLogin,
      login,
      changePassword,
      logout,
      refreshProfile,
    }),
    [accessToken, changePassword, isAuthenticated, isLoading, login, logout, refreshProfile, startGoogleLogin, startMicrosoftLogin, startSsoLogin, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
